/**
 * Log levels for categorizing log messages
 */
export var LogLevel;
(function (LogLevel) {
    LogLevel[LogLevel["DEBUG"] = 0] = "DEBUG";
    LogLevel[LogLevel["INFO"] = 1] = "INFO";
    LogLevel[LogLevel["WARN"] = 2] = "WARN";
    LogLevel[LogLevel["ERROR"] = 3] = "ERROR";
    LogLevel[LogLevel["CRITICAL"] = 4] = "CRITICAL";
})(LogLevel || (LogLevel = {}));
/**
 * Log categories for better organization
 */
export var LogCategory;
(function (LogCategory) {
    LogCategory["AUTH"] = "Authentication";
    LogCategory["DATABASE"] = "Database";
    LogCategory["STORAGE"] = "Storage";
    LogCategory["UI"] = "User Interface";
    LogCategory["NETWORK"] = "Network";
    LogCategory["PERFORMANCE"] = "Performance";
    LogCategory["SECURITY"] = "Security";
    LogCategory["GENERAL"] = "General";
})(LogCategory || (LogCategory = {}));
class Logger {
    constructor() {
        Object.defineProperty(this, "logs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "maxLogs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 100
        });
        Object.defineProperty(this, "minLevel", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: LogLevel.INFO
        });
        // Load settings from localStorage if available
        this.loadSettings();
        // Load existing logs from localStorage
        this.loadLogs();
    }
    loadSettings() {
        try {
            const savedMinLevel = localStorage.getItem('logger_minLevel');
            if (savedMinLevel) {
                this.minLevel = parseInt(savedMinLevel);
            }
            const savedMaxLogs = localStorage.getItem('logger_maxLogs');
            if (savedMaxLogs) {
                this.maxLogs = parseInt(savedMaxLogs);
            }
        }
        catch (e) {
            console.error('Error loading logger settings:', e);
        }
    }
    saveSettings() {
        try {
            localStorage.setItem('logger_minLevel', this.minLevel.toString());
            localStorage.setItem('logger_maxLogs', this.maxLogs.toString());
        }
        catch (e) {
            console.error('Error saving logger settings:', e);
        }
    }
    loadLogs() {
        try {
            const savedLogs = localStorage.getItem('application_logs');
            if (savedLogs) {
                this.logs = JSON.parse(savedLogs);
            }
        }
        catch (e) {
            console.error('Error loading logs:', e);
        }
    }
    setMinLevel(level) {
        this.minLevel = level;
        this.saveSettings();
    }
    setMaxLogs(max) {
        this.maxLogs = max;
        this.saveSettings();
    }
    addLog(level, message, category = LogCategory.GENERAL, context, userId, sessionId) {
        if (level < this.minLevel)
            return;
        const entry = {
            timestamp: new Date().toISOString(),
            level,
            category,
            message,
            context,
            userId,
            sessionId
        };
        // Add to in-memory logs
        this.logs.push(entry);
        // Trim logs if exceeding max
        if (this.logs.length > this.maxLogs) {
            this.logs = this.logs.slice(-this.maxLogs);
        }
        // Also log to console
        this.logToConsole(entry);
        // Store in localStorage
        this.persistLogs();
    }
    logToConsole(entry) {
        const prefix = `[${entry.timestamp}] [${LogLevel[entry.level]}]`;
        switch (entry.level) {
            case LogLevel.DEBUG:
                console.debug(prefix, entry.message, entry.context || '');
                break;
            case LogLevel.INFO:
                console.info(prefix, entry.message, entry.context || '');
                break;
            case LogLevel.WARN:
                console.warn(prefix, entry.message, entry.context || '');
                break;
            case LogLevel.ERROR:
                console.error(prefix, entry.message, entry.context || '');
                break;
        }
    }
    persistLogs() {
        try {
            localStorage.setItem('application_logs', JSON.stringify(this.logs));
        }
        catch (e) {
            console.error('Error persisting logs:', e);
        }
    }
    getLogs() {
        return [...this.logs];
    }
    clearLogs() {
        this.logs = [];
        localStorage.removeItem('application_logs');
    }
    /**
     * Log a debug message
     */
    debug(message, categoryOrContext, context, userId, sessionId) {
        const category = this.getCategoryFromParam(categoryOrContext);
        const ctx = this.getContextFromParam(categoryOrContext, context);
        this.addLog(LogLevel.DEBUG, message, category, ctx, userId, sessionId);
    }
    /**
     * Log an informational message
     */
    info(message, categoryOrContext, context, userId, sessionId) {
        const category = this.getCategoryFromParam(categoryOrContext);
        const ctx = this.getContextFromParam(categoryOrContext, context);
        this.addLog(LogLevel.INFO, message, category, ctx, userId, sessionId);
    }
    /**
     * Log a warning message
     */
    warn(message, categoryOrContext, context, userId, sessionId) {
        const category = this.getCategoryFromParam(categoryOrContext);
        const ctx = this.getContextFromParam(categoryOrContext, context);
        this.addLog(LogLevel.WARN, message, category, ctx, userId, sessionId);
    }
    /**
     * Log an error message
     */
    error(message, categoryOrContext, context, userId, sessionId) {
        const category = this.getCategoryFromParam(categoryOrContext);
        const ctx = this.getContextFromParam(categoryOrContext, context);
        this.addLog(LogLevel.ERROR, message, category, ctx, userId, sessionId);
    }
    /**
     * Log a critical error message
     */
    critical(message, categoryOrContext, context, userId, sessionId) {
        const category = this.getCategoryFromParam(categoryOrContext);
        const ctx = this.getContextFromParam(categoryOrContext, context);
        this.addLog(LogLevel.CRITICAL, message, category, ctx, userId, sessionId);
    }
    /**
     * Helper method to determine if a parameter is a LogCategory
     */
    isLogCategory(param) {
        return typeof param === 'string' && Object.values(LogCategory).includes(param);
    }
    /**
     * Helper method to extract category from parameter
     */
    getCategoryFromParam(param) {
        if (this.isLogCategory(param)) {
            return param;
        }
        return LogCategory.GENERAL;
    }
    /**
     * Helper method to extract context from parameters
     */
    getContextFromParam(categoryOrContext, context) {
        if (!categoryOrContext) {
            return context;
        }
        if (this.isLogCategory(categoryOrContext)) {
            return context;
        }
        return categoryOrContext;
    }
}
// Export a singleton instance
export const logger = new Logger();
